import { Check, X, Pause, Play, ArrowRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  useConnection,
  useAcceptConnection,
  useSuspendConnection,
  useReactivateConnection,
  useCloseConnection,
  useUpdateConnection,
} from "@/hooks/useConnections";
import { statusBadgeVariant } from "./helpers";


// ─── Connection drawer ────────────────────────────────────────────

export function ConnectionDrawer({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data: connection, isLoading } = useConnection(id);
  const accept = useAcceptConnection();
  const suspend = useSuspendConnection();
  const reactivate = useReactivateConnection();
  const close = useCloseConnection();
  const update = useUpdateConnection();

  return (
    <Sheet open={!!id} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        {isLoading || !connection ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {
                  (connection.myRole === "distributor"
                    ? connection.vendor
                    : connection.distributor
                  ).displayName
                }
                <Badge variant={statusBadgeVariant(connection.status)}>
                  {connection.status}
                </Badge>
              </SheetTitle>
              <SheetDescription>
                You are{" "}
                <strong className="font-medium">{connection.myRole}</strong> ·{" "}
                {connection.vendorOnEaze ? "On Eaze" : "External vendor"}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Settings */}
              <section>
                <h4 className="text-sm font-medium mb-3">Settings</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">
                        Auto-post journals on close
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        When a handshake closes, post sale + receipt journals
                        atomically.
                      </p>
                    </div>
                    <Switch
                      checked={connection.autoPostOnClose}
                      disabled={
                        connection.status !== "active" &&
                        connection.status !== "suspended"
                      }
                      onCheckedChange={(checked) =>
                        update.mutate(
                          {
                            id: connection._id,
                            input: { autoPostOnClose: checked },
                          },
                          {
                            onSuccess: () => toast.success("Settings updated"),
                            onError: (e: any) =>
                              toast.error(
                                e?.response?.data?.message || "Failed",
                              ),
                          },
                        )
                      }
                    />
                  </div>
                </div>
              </section>

              <Separator />

              {/* Contact info */}
              <section>
                <h4 className="text-sm font-medium mb-3">
                  {connection.myRole === "distributor"
                    ? "Vendor"
                    : "Distributor"}{" "}
                  contact
                </h4>
                <div className="text-sm space-y-1 text-muted-foreground">
                  {(() => {
                    const o =
                      connection.myRole === "distributor"
                        ? connection.vendor
                        : connection.distributor;
                    return (
                      <>
                        {o.contactName && <div>{o.contactName}</div>}
                        {o.contactPhone && <div>{o.contactPhone}</div>}
                        {o.contactEmail && <div>{o.contactEmail}</div>}
                      </>
                    );
                  })()}
                </div>
              </section>

              <Separator />

              {/* Status history */}
              <section>
                <h4 className="text-sm font-medium mb-3">Status history</h4>
                <ul className="space-y-2 text-sm">
                  {connection.statusHistory.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">
                          {h.from ? `${h.from} → ${h.to}` : h.to}
                        </div>
                        {h.reason && (
                          <div className="text-xs text-muted-foreground">
                            {h.reason}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(h.at), {
                            addSuffix: true,
                          })}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              <Separator />

              {/* Actions */}
              <section className="space-y-2">
                {connection.status === "pending" &&
                  connection.initiatedBy !== connection.myRole && (
                    <Button
                      className="w-full"
                      onClick={() =>
                        accept.mutate(connection._id, {
                          onSuccess: () => toast.success("Connection accepted"),
                          onError: (e: any) =>
                            toast.error(e?.response?.data?.message || "Failed"),
                        })
                      }
                    >
                      <Check className="h-4 w-4 mr-2" /> Accept connection
                    </Button>
                  )}
                {connection.status === "active" && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const reason = prompt(
                        "Reason for suspending (optional):",
                      );
                      if (reason === null) return;
                      suspend.mutate(
                        { id: connection._id, reason },
                        {
                          onSuccess: () =>
                            toast.success("Connection suspended"),
                          onError: (e: any) =>
                            toast.error(e?.response?.data?.message || "Failed"),
                        },
                      );
                    }}
                  >
                    <Pause className="h-4 w-4 mr-2" /> Suspend
                  </Button>
                )}
                {connection.status === "suspended" && (
                  <Button
                    className="w-full"
                    onClick={() =>
                      reactivate.mutate(connection._id, {
                        onSuccess: () =>
                          toast.success("Connection reactivated"),
                        onError: (e: any) =>
                          toast.error(e?.response?.data?.message || "Failed"),
                      })
                    }
                  >
                    <Play className="h-4 w-4 mr-2" /> Reactivate
                  </Button>
                )}
                {connection.status !== "closed" &&
                  connection.initiatedBy === connection.myRole && (
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => {
                        if (
                          !confirm(
                            "Close this connection? Existing handshakes will continue but no new ones can be created.",
                          )
                        )
                          return;
                        const reason = prompt("Reason (optional):");
                        close.mutate(
                          { id: connection._id, reason: reason || undefined },
                          {
                            onSuccess: () => {
                              toast.success("Connection closed");
                              onClose();
                            },
                            onError: (e: any) =>
                              toast.error(
                                e?.response?.data?.message || "Failed",
                              ),
                          },
                        );
                      }}
                    >
                      <X className="h-4 w-4 mr-2" /> Close connection
                    </Button>
                  )}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}